<template>
    <div>
        <DCard padding>
            <DRow
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol
                    sm="12"
                    class="text-right"
                >
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        @click="showListViewModal = true"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DTable
                        :items="orders.items"
                        :fields="getFields"
                        :sorter="isSortable"
                        :sorter-value="sorterValue"
                        class="table-responsive"
                        @update:sorter-value="updateOrdering"
                    >
                        <template
                            v-if="canManagePerson"
                            #checkbox-header
                        >
                            <DFormCheck
                                id="order_checkbox_all"
                                v-model="checkAllItems"
                                inline
                                @update:modelValue="updateAllCheckboxes"
                            />
                        </template>
                        <template #checkbox="{item}">
                            <td>
                                <DFormCheck
                                    v-if="item.client === 'PSA'"
                                    id="order_checkbox_row"
                                    :checked="form.items.includes(item.id)"
                                    inline
                                    @update:modelValue="updateCheckboxArray($event, item.id)"
                                />
                            </td>
                        </template>
                        <template #id="{item}">
                            <td>
                                <Link
                                    v-if="userPermissions.includes('order.view')"
                                    :href="$route('orders.details', orderDetailsRouteParams(item))"
                                >
                                    {{ item.id }}
                                </Link>
                                <span
                                    v-else
                                    class="text-capitalize"
                                >{{ item.id }}</span>
                            </td>
                        </template>
                        <template #incident="{item}">
                            <td>
                                <IncidentFlag
                                    v-if="userPermissions.includes('customer.incidents_view') && item.has_incidents"
                                    id="orders-incident-flag"
                                    :active-incident="item.has_open_incidents"
                                    :relation-type="'orders'"
                                    :relation-id="item.id"
                                    :customer-id="item.customer_id"
                                />
                            </td>
                        </template>
                        <template #order_no="{item}">
                            <td>
                                {{ getValue(item.order_no) }}
                            </td>
                        </template>
                        <template #product="{item}">
                            <td>
                                <ShowMore
                                    :items="item.product"
                                />
                            </td>
                        </template>
                        <template #order_person="{item}">
                            <td>
                                {{ getValue(item.order_person) }}
                            </td>
                        </template>
                        <template #sales_representative="{item}">
                            <td>
                                {{ getValue(item.sales_representative) }}
                            </td>
                        </template>
                        <template #finalizedAt="{item}">
                            <td>
                                {{ setDateFormat(item.finalizedAt) }}
                            </td>
                        </template>
                        <template #date="{item}">
                            <td>
                                {{ setDateFormat(item.date) }}
                            </td>
                        </template>
                        <template #created_at="{item}">
                            <td>
                                {{ setDateFormat(item.created_at) }}
                            </td>
                        </template>
                        <template #client="{item}">
                            <td>
                                {{ getValue(item.client) }}
                            </td>
                        </template>
                        <template #active_contracts="{item}">
                            <td>
                                <div
                                    v-for="(id, index) in item.active_contracts"
                                    :key="index"
                                >
                                    <Link
                                        v-if="userPermissions.includes('contract.view')"
                                        :href="$route('customers.customers.contracts.show', {customer: customer.id, contract: id})"
                                    >
                                        {{ id + (index < item.active_contracts.length - 1 ? ',' : ' ') }}
                                    </Link>
                                    <span
                                        v-else
                                    >{{ id + (index < item.active_contracts.length - 1 ? ',' : ' ') }}</span>
                                </div>
                            </td>
                        </template>
                    </DTable>
                    <DButton
                        v-if="canManagePerson && userPermissions.includes('order.accept')"
                        class="manage-person-button"
                        color="primary"
                        :label="__('customers.orders.manage_order_person')"
                        @click="showManageOrderPersonModal=true"
                        @close="showManageOrderPersonModal=false"
                    />
                    <DPagination
                        v-if="orders.last_page > 1"
                        :active-page="orders.current_page"
                        :pages="orders.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
        </DCard>
        <ManageOrderPersonModal
            v-if="showManageOrderPersonModal"
            :contacts="contacts"
            :show-modal="showManageOrderPersonModal"
            @confirm="manageOrderPerson"
            @close-modal="showManageOrderPersonModal=false"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
        </DFabs>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import ManageOrderPersonModal from '~@/Components/Customers/Orders/ManageOrderPersonModal'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import ShowMore from '~@/Components/Customers/Orders/ShowMore'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import orderNumber from '~@/Mixins/orderNumber'

export default {
    name: 'CustomerOrderIndex',
    components: {
        ListViewModal,
        ManageOrderPersonModal,
        CreateCloudPitCustomerModal,
        SSOFab,
        ShowMore,
        IncidentFlag
    },
    mixins: [orderNumber],
    layout: [App, CustomerBase],
    props: {
        orders: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        contacts: {
            type: Array,
            default: () => { return [] }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        filters: {
            type: Object,
            default: () => {
                return {}
            }
        }
    },
    data () {
        return {
            showCreateCloudpitCustomerModal: false,
            form: this.$inertia.form({
                items: [],
                contact: null,
                customer: this.customer.id
            }),
            checkAllItems: false,
            showManageOrderPersonModal: false,
            filterParams: {
                search: this.filters.search ?? '',
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc ? 1 : 0
            },
            showListViewModal: false,
            canManagePerson: false,
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        contactList () {
            return this.contacts.map(contact => {
                return {
                    value: contact.id,
                    label: contact.firstname + ' ' + contact.lastname
                }
            })
        },
        sorterValue () {
            return {
                column: this.filterParams.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'checkbox' ? this.__('settings.list_view.customer-order-list.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            this.checkManagePerson()

            return availableList
        }
    },
    methods: {
        checkManagePerson () {
            this.canManagePerson = false
            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    if (item.column === 'checkbox') {
                        this.canManagePerson = true
                    }
                }
            })
        },
        changePage (page) {
            const query = Object.fromEntries(Object.entries(this.filterParams).filter(([_, v]) => v))

            if (page > 1) {
                query.page = page
            }

            this.$inertia.get(this.$route('customers.customers.orders.index', {
                customer: this.customer.id
            }), query)
        },
        updateAllCheckboxes (value) {
            this.form.items = []
            if (value === false) {
                return
            }

            this.orders.items.forEach(item => {
                if (item.client === 'PSA') {
                    this.form.items.push(item.id)
                }
            }
            )
        },
        updateCheckboxArray (value, id) {
            const itemIndex = this.form.items.indexOf(id)
            if (itemIndex < 0) {
                this.form.items.push(id)
                return
            }

            this.form.items.splice(itemIndex, 1)
        },
        manageOrderPerson (contact) {
            this.form.contact = contact
            this.form.post(this.$route('orders.psa.manage_order_person'), {
                onSuccess: () => {
                    this.form.items = []
                    this.checkAllItems = false
                    this.showManageOrderPersonModal = false
                },
                onError: () => {
                    this.showManageOrderPersonModal = false
                }
            })
        },
        updateOrdering (event) {
            this.filterParams.order_by = event.column
            this.filterParams.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.orders.current_page)
        },
        getValue (value) {
            return value ?? ''
        },
        orderRoutePrefix (item) {
            // TODO Need to refactor Show pages (we currently have OSA and PSA), should have one, for OneWebshop and WooCommerce
            // we do not have those pages, or we also need to create new show pages for OneWebshop and WooCommerce.
            // Also what to do with internal orders, to which page those orders need to go
            if (item.client === null || item.client === 'OneWebshop' || item.client === 'WooCommerce') {
                return 'psa'
            }

            return item.client.toLowerCase()
        }
    }
}
</script>

<style lang="scss" scoped>
.manage-person-button {
    margin: 20px 0 20px 20px
}
</style>
